import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import FormInputField from 'src/components/common/formInputField';
import { useAppearanceActions } from 'src/store/appearance/actions';
import { RootState } from 'src/store/reducers/state';
import Select from 'react-select';
import { SelectModel } from 'src/models/videosModel';
import { AddNavigationModel } from 'src/models/appearanceModel';

interface IProps {
  navigationAddFlag?: AddNavigationModel;
}

const defaultProps: IProps = {
  navigationAddFlag: {
    saveFlag: '',
    navigationId: ''
  }
};
interface CustomLink {
  linkName: string;
  linkURL: string;
  linkTarget: string;
}

const CustomLinksInput: React.FC<{
  control: any;
  watch: any;
  setValue: any;
}> = ({ control, watch, setValue }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customChildLinks'
  });

  return (
    <div className="space-y-2">
      {fields.map((field, index) => (
        <div key={field.id} className="border rounded p-3 bg-gray-50">
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">Link Name</label>
              <input
                className="input-style w-full"
                placeholder="Link Name"
                {...control.register(`customChildLinks.${index}.linkName`, {
                  required: true
                })}
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block">URL</label>
              <input
                className="input-style w-full"
                placeholder="https://..."
                {...control.register(`customChildLinks.${index}.linkURL`, {
                  required: true
                })}
              />
            </div>
            <div className="w-28">
              <label className="text-xs text-gray-500 mb-1 block">Target</label>
              <select
                className="input-style w-full"
                {...control.register(`customChildLinks.${index}.linkTarget`)}>
                <option value="_Self">_self</option>
                <option value="_Blank">_blank</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-300 hover:bg-red-50 text-sm">
                <i className="fas fa-trash" />
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => append({ linkName: '', linkURL: '', linkTarget: '_Self' })}
        className="px-4 btn-primary w-auto whitespace-nowrap mt-1 text-sm">
        <i className="fas fa-plus pr-1" />
        Add Link
      </button>
    </div>
  );
};
const AddNavigationMenu: React.FC<IProps> = ({ navigationAddFlag }) => {
  const dispatch = useDispatch();
  const appearanceAction = useAppearanceActions(dispatch);
  const [selectedMethod, setSelectedMethod] = useState<string>();
  const [finalChannelList, setFinalChannelList] = useState<SelectModel[]>();
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [linkTarget, setselectedLinkTarget] = useState<string>('');
  const [methods, setMethods] = useState([
    { id: 3, name: 'Select Method' },
    { id: 0, name: 'Link' },
    { id: 1, name: 'Channel' },
    { id: 2, name: 'Category' }
  ]);
  const btnRef = useRef<HTMLButtonElement>(null);

  const { channels, categoriesList } = useSelector((state: RootState) => {
    return {
      channels: state.channel.channels,
      categoriesList: state.category.categoriesList
    };
  });

  const { isNavigationAdded, isNavigationUpdated, singleNavigations } = useSelector(
    (state: RootState) => {
      return {
        isNavigationAdded: state.appearance.isNavigationAdded,
        singleNavigations: state.appearance.singleNavigations,
        isNavigationUpdated: state.appearance.isNavigationUpdated
      };
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
    watch
  } = useForm();

  useEffect(() => {
    if (channels && channels.length) {
      const options = channels.map((item: any) => ({
        value: item.id,
        label: item.title
      }));
      const finalChannelOptions = [{ value: '', label: 'Select Channel' }, ...options];
      setFinalChannelList(finalChannelOptions);
    }
  }, [channels]);

  useEffect(() => {
    if (methods) {
      setSelectedMethod(methods[0].id.toString());
    }
  }, []);

  useEffect(() => {
    if (isNavigationAdded || isNavigationUpdated) {
      reset({
        module: ''
      });
      if (btnRef.current) {
        btnRef.current.click();
      }
      appearanceAction.resetAppearanceStateRequest();
      appearanceAction.getNavigationRequest();
    }
  }, [isNavigationAdded, isNavigationUpdated]);

  useEffect(() => {
    if (singleNavigations) {
      reset(singleNavigations);
      setSelectedChannel(singleNavigations.selectedChannel || '');
      setSelectedMethod(singleNavigations.method?.toString() || '');
      setselectedLinkTarget(singleNavigations.linkTarget?.toString() || '');
    }
  }, [singleNavigations]);

  // Helper function to get child categories based on selected category
  const getChildCategories = () => {
    const selectedCategoryId = watch('selectedCategory');
    if (!selectedCategoryId || !categoriesList?.categories) return [];

    return categoriesList.categories.filter(
      (category: any) => category.parentCategoryId === selectedCategoryId
    );
  };

  // Helper function to get child channels based on selected channel
  const getChildChannels = () => {
    const selectedChannelId = selectedChannel;
    if (!selectedChannelId || !channels) return [];

    const collectAllChildren = (children: any[], level = 0): any[] => {
      const result: any[] = [];
      for (const child of children) {
        const indent = '  '.repeat(level);
        result.push({
          ...child,
          displayTitle: `${indent}${child.title}`
        });

        if (child.children && child.children.length > 0) {
          result.push(...collectAllChildren(child.children, level + 1));
        }
      }
      return result;
    };

    const findChannelWithChildren = (channelList: any[]): any[] => {
      for (const channel of channelList) {
        if (channel.id === selectedChannelId) {
          return collectAllChildren(channel.children || []);
        }
        if (channel.children && channel.children.length > 0) {
          const found = findChannelWithChildren(channel.children);
          if (found.length > 0) return found;
        }
      }
      return [];
    };

    return findChannelWithChildren(channels);
  };

  const onSubmit = (data: any) => {
    if (navigationAddFlag?.saveFlag === 'Add') {
      let payload;
      if (data.selectedChannel) {
        payload = { ...data, selectedChannel: selectedChannel };
      } else {
        payload = { ...data };
      }
      appearanceAction.saveNavigationRequest(payload);
    } else {
      const customChildLinks = (watch('customChildLinks') || []).map((link: any) => ({
        id: link.id || null,
        linkName: link.linkName,
        linkURL: link.linkURL,
        linkTarget: link.linkTarget
      }));
      const payloadData = {
        navigationId: data.id,
        updateData: [
          { op: 'replace', path: 'linkName', value: watch('linkName') },
          { op: 'replace', path: 'linkURL', value: watch('linkURL') },
          { op: 'replace', path: 'linkTarget', value: watch('linkTarget') },
          { op: 'replace', path: 'method', value: watch('method') },
          {
            op: 'replace',
            path: 'childrenCategories',
            value: watch('childrenCategories')
          },
          { op: 'replace', path: 'addChildren', value: watch('addChildren') },
          { op: 'replace', path: 'autheticationRequired', value: watch('autheticationRequired') },
          { op: 'replace', path: 'selectedCategory', value: watch('selectedCategory') },
          { op: 'replace', path: 'selectedChannel', value: watch('selectedChannel') },
          { op: 'replace', path: 'children', value: watch('children') },
          { op: 'replace', path: 'customChildLinks', value: customChildLinks }
        ]
      };
      appearanceAction.updateNavigationRequest(payloadData);
    }
  };

  const handleChangeMethod = (e: any) => {
    const newMethod = e.target.value;
    const prevMethod = selectedMethod;

    setSelectedMethod(newMethod);

    if (newMethod !== prevMethod) {
      if (newMethod === '1') {
        setValue('selectedCategory', '');
        setValue('childrenCategories', '');
        setValue('children', '');
      } else if (newMethod === '2') {
        setValue('selectedChannel', '');
        setValue('children', '');
        setValue('childrenCategories', '');
      } else {
        setValue('selectedChannel', '');
        setValue('selectedCategory', '');
        setValue('children', '');
        setValue('childrenCategories', '');
      }
    }
  };

  const handleCloseClick = () => {
    reset({
      linkName: '',
      method: '0',
      linkURL: '',
      selectedChannel: '',
      selectedCategory: '',
      linkTarget: '_Self',
      autheticationRequired: '',
      addChildren: false,
      childrenCategories: ''
    });
    setSelectedMethod('3');
  };

  return (
    <div>
      <div
        className="modal fade"
        id="AddnavigationModal"
        aria-labelledby="AddnavigationModalLabel"
        aria-hidden="true">
        <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
          <div className="modal-content">
            <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 rounded-t-md">
              <h5
                className="text-xl font-semibold leading-normal text-center w-full"
                id="AddnavigationModalLabel">
                {navigationAddFlag?.saveFlag === 'Add' ? 'Add' : 'Edit'} Navigation Link
              </h5>
              <button
                type="button"
                className="dismiss-btn"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => handleCloseClick()}
                ref={btnRef}></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body relative py-4 px-10">
                <div className={`form-group mb-3`}>
                  <FormInputField
                    label={'Link Name'}
                    type={'text'}
                    fieldName={'linkName'}
                    register={register}
                    errors={errors}
                    isRequired={true}
                  />
                </div>

                <div className={`form-group mb-3`}>
                  <label className="font-semobold mb-1">Method</label>
                  <select
                    className="input-style"
                    {...register('method')}
                    value={selectedMethod}
                    onChange={(e) => handleChangeMethod(e)}>
                    {methods.map((method, idx) => (
                      <option value={method.id} key={idx}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedMethod === '0' ? (
                  <div className={`form-group mb-3`}>
                    <FormInputField
                      label={'Link URL'}
                      type={'text'}
                      fieldName={'linkURL'}
                      register={register}
                      errors={errors}
                      isRequired={true}
                    />
                  </div>
                ) : selectedMethod === '1' ? (
                  <div className={`form-group mb-3`}>
                    <label className="font-semobold mb-1">Selected Channel</label>
                    <Controller
                      name="selectedChannel"
                      control={control}
                      render={({ field }) => (
                        <Select
                          options={finalChannelList}
                          value={finalChannelList?.find((opt) => opt.value === selectedChannel) || null}
                          onChange={(selected: any) => {
                            setSelectedChannel(selected?.value || '');
                            field.onChange(selected?.value || '');
                          }}
                          placeholder="Select Channel"
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                  </div>
                ) : selectedMethod === '2' ? (
                  <div className={`form-group mb-3`}>
                    <label className="font-semobold mb-1">Selected Category</label>
                    {categoriesList && categoriesList?.categories?.length && (
                      <Controller
                        name="selectedCategory"
                        control={control}
                        render={({ field }) => {
                          const categoryOptions = categoriesList.categories.map((item: any) => ({
                            value: item.id,
                            label: item.category
                          }));
                          return (
                            <Select
                              options={categoryOptions}
                              value={categoryOptions.find((opt) => opt.value === field.value) || null}
                              onChange={(selected: any) => {
                                field.onChange(selected?.value || '');
                              }}
                              placeholder="Select Category"
                              classNamePrefix="react-select"
                            />
                          );
                        }}
                      />
                    )}
                  </div>
                ) : (
                  ''
                )}
                <div className={`form-group mb-3`}>
                  <label className="font-semobold mb-1">Link Target</label>
                  <select
                    className="input-style"
                    {...register('linkTarget')}
                    value={linkTarget}
                    onChange={(e) => setselectedLinkTarget(e.target.value)}>
                    <option value={'_Self'}>_self</option>
                    <option value={'_Blank'}>_blank</option>
                  </select>
                </div>
                <div className={`form-group mb-3`}>
                  <div className="form-check flex items-center justify-start">
                    <input
                      className="form-check-input check-box-input focus:shadow-none transition duration-200 bg-no-repeat bg-center bg-contain float-left checked:bg-blue-600 checked:border-blue-600"
                      type="checkbox"
                      {...register('autheticationRequired')}
                    />
                    <label className="ml-1">Authetication Required</label>
                  </div>
                </div>
                <div className={`form-group mb-3`}>
                  <div className="form-check flex items-center justify-start">
                    <input
                      className="form-check-input check-box-input focus:shadow-none transition duration-200 bg-no-repeat bg-center bg-contain float-left checked:bg-blue-600 checked:border-blue-600"
                      type="checkbox"
                      {...register('addChildren')}
                    />
                    <label className="ml-1">Add Children</label>
                  </div>
                </div>
                {watch('addChildren') ? (
                  <div className={`form-group mb-3`}>
                    {selectedMethod === '1' ? (
                      <>
                        <label className="font-semobold mb-1">Child Channels</label>
                        {(() => {
                          const childChannels = getChildChannels();
                          return childChannels.length > 0 ? (
                            <Controller
                              name="children"
                              control={control}
                              render={({ field }) => {
                                const options = childChannels.map((item: any) => ({
                                  value: item.id,
                                  label: item.displayTitle || item.title
                                }));
                                return (
                                  <Select
                                    {...field}
                                    isMulti
                                    options={options}
                                    classNamePrefix="react-select"
                                    placeholder="Select Child Channel(s)"
                                    onChange={(selected: any) => {
                                      field.onChange(
                                        selected ? selected.map((s: any) => s.value).join(',') : ''
                                      );
                                    }}
                                    value={
                                      field.value
                                        ? options.filter((option: any) =>
                                          field.value.split(',').includes(option.value.toString())
                                        )
                                        : []
                                    }
                                  />
                                );
                              }}
                            />
                          ) : (
                            <p className="text-sm text-gray-500">
                              No child channels available for the selected channel.
                            </p>
                          );
                        })()}
                      </>
                    ) : selectedMethod === '2' ? (
                      <>
                        <label className="font-semobold mb-1">Child Categories</label>
                        {(() => {
                          const childCategories = getChildCategories();
                          return childCategories.length > 0 ? (
                            <Controller
                              name="childrenCategories"
                              control={control}
                              render={({ field }) => {
                                const options = childCategories.map((item: any) => ({
                                  value: item.id,
                                  label: item.category
                                }));
                                return (
                                  <Select
                                    {...field}
                                    isMulti
                                    options={options}
                                    classNamePrefix="react-select"
                                    placeholder="Select Child Category(s)"
                                    onChange={(selected: any) => {
                                      field.onChange(
                                        selected ? selected.map((s: any) => s.value).join(',') : ''
                                      );
                                    }}
                                    value={
                                      field.value
                                        ? options.filter((option: any) =>
                                          field.value.split(',').includes(option.value.toString())
                                        )
                                        : []
                                    }
                                  />
                                );
                              }}
                            />
                          ) : (
                            <p className="text-sm text-gray-500">
                              No child categories available for the selected category.
                            </p>
                          );
                        })()}
                      </>
                    ) : selectedMethod === '0' ? (
                      <>
                        <label className="font-semobold mb-1">Child Links</label>
                        <CustomLinksInput control={control} watch={watch} setValue={setValue} />
                      </>
                    ) : null}
                  </div>
                ) : ''}
              </div>

              <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-center p-4 pb-6 pt-0 rounded-b-md">
                <button
                  type="submit"
                  className="px-6 btn-primary w-auto whitespace-nowrap mr-2 ml-0 mt-0">
                  {navigationAddFlag?.saveFlag === 'Add' ? 'Add' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="mx-0 px-6 btn-primary discard-btn w-auto whitespace-nowrap mt-0"
                  data-bs-dismiss="modal"
                  onClick={() => handleCloseClick()}>
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
AddNavigationMenu.defaultProps = defaultProps;

export default AddNavigationMenu;
